import React, { useState } from "react";
import {
  Container,
  Typography,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Chip,
} from "@mui/material";
import Layout from "../components/layout/GSTlayout";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

// Sample payment data (you'll replace this with actual data)
const paymentData = [
  {
    id: 1,
    month: "January",
    amount: 50000,
    status: "Paid",
    dueDate: "2024-01-15",
    paymentDate: "2024-01-10",
  },
  {
    id: 2,
    month: "February",
    amount: 55000,
    status: "Paid",
    dueDate: "2024-02-15",
    paymentDate: "2024-02-12",
  },
  {
    id: 3,
    month: "March",
    amount: 52000,
    status: "Pending",
    dueDate: "2024-03-15",
    paymentDate: null,
  },
];

// Payment Trends Chart Component
const PaymentTrendsChart = () => {
  const chartData = {
    labels: paymentData.map((item) => item.month),
    datasets: [
      {
        label: "Monthly GST Payments",
        data: paymentData.map((item) => item.amount),
        borderColor: "rgb(75, 192, 192)",
        tension: 0.1,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: "top",
      },
      title: {
        display: true,
        text: "GST Payment Trends",
      },
    },
  };

  return <Line data={chartData} options={chartOptions} />;
};

// Payment Status Summary Component
const PaymentStatusSummary = () => {
  const totalPayments = paymentData.reduce(
    (sum, payment) => sum + payment.amount,
    0
  );
  const paidPayments = paymentData.filter((p) => p.status === "Paid");
  const pendingPayments = paymentData.filter((p) => p.status === "Pending");

  return (
    <Box sx={{ display: "flex", justifyContent: "space-between", my: 2 }}>
      <Chip
        label={`Total Payments: ₹${totalPayments.toLocaleString()}`}
        color="primary"
        variant="outlined"
      />
      <Chip
        label={`Paid: ${paidPayments.length}`}
        color="success"
        variant="outlined"
      />
      <Chip
        label={`Pending: ${pendingPayments.length}`}
        color="warning"
        variant="outlined"
      />
    </Box>
  );
};

// Main Payment Tracking Component
const PaymentTracking = () => {
  return (
    <Layout>
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Typography variant="h4" gutterBottom>
          GST Payment Tracking
        </Typography>

        <Grid container spacing={3}>
          {/* Payment Trends Chart */}
          <Grid item xs={12} md={8}>
            <Paper sx={{ p: 2, display: "flex", flexDirection: "column" }}>
              <PaymentTrendsChart />
            </Paper>
          </Grid>

          {/* Payment Status Summary */}
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 2, display: "flex", flexDirection: "column" }}>
              <Typography variant="h6">Payment Summary</Typography>
              <PaymentStatusSummary />
            </Paper>
          </Grid>

          {/* Payment History Table */}
          <Grid item xs={12}>
            <Paper sx={{ p: 2, width: "100%" }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Payment History
              </Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Month</TableCell>
                      <TableCell align="right">Amount (₹)</TableCell>
                      <TableCell>Due Date</TableCell>
                      <TableCell>Payment Date</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paymentData.map((payment) => (
                      <TableRow key={payment.id}>
                        <TableCell>{payment.month}</TableCell>
                        <TableCell align="right">
                          {payment.amount.toLocaleString()}
                        </TableCell>
                        <TableCell>{payment.dueDate}</TableCell>
                        <TableCell>
                          {payment.paymentDate || "Not Paid"}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={payment.status}
                            color={
                              payment.status === "Paid"
                                ? "success"
                                : payment.status === "Pending"
                                ? "warning"
                                : "default"
                            }
                            size="small"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Layout>
  );
};

export default PaymentTracking;
